import { defineComponent } from 'vue';
import {
    QIcon,
    QBtn,
    QTooltip,
    QSelect,
    QSpace,
    QMenu,
    QList,
    QItem,
    QItemSection,
    QItemLabel,
} from 'quasar';
import utils from '../../../Shared/utils.jsx';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import ElapsedTime from '../vuecontrols/elapsedTimer.vue';

const generalCallControlsStyle = {
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomWidth: '1px',
    borderBottomColor: 'var(--v-lightGreyBorder-base)',
    borderBottomStyle: 'solid',
    backgroundColor: 'var(--v-pageBackground-base)',
    display: 'flex',
    alignItems: 'center',
};

const callControlsContainerStyle = {
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const callRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    backgroundColor: '#a7e2a5',
};

export default defineComponent({
    name: 'soft-phone-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        QIcon,
        QBtn,
        QTooltip,
        QSelect,
        QSpace,
        QMenu,
        QList,
        QItem,
        QItemSection,
        QItemLabel,
        ElapsedTime,
    },
    data() {
        return {
            previewCallStart: Date.now(),
            previewFromOptions: [
                { text: 'Main caller ID', value: 'preview-from' },
            ],
            previewFromValue: 'preview-from',
            previewDialOption: {
                title: '(555) 010-0199',
                subtitle: 'Search / Dial preview',
            },
        };
    },
    computed: {
        rootStyle() {
            return {
                ...utils.resolveStyleHints(this.controlData?.StyleHints, this),
                ...utils.getSize(this.controlData?.SizeOptions, this.parentType, this.$parent),
                display: 'flex',
                flexDirection: 'column',
                minWidth: '280px',
            };
        },
        phoneTitleText() {
            return this.controlData?.PreviewPhoneTitle || 'Alex Agent x.1001';
        },
    },
    render() {
        if (!this.controlData)
            return null;

        return (
            <div
                class={{
                    'designer-container': true,
                    selected: this.selected$,
                }}
                style={this.rootStyle}
                onClick={(e) => this.handleClick(e)}>
                <div class={{ 'designer-container-hovering': true, 'designer-container-selected': this.selected$ }} />
                <div
                    class={{ 'c-SoftPhone': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                    style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
                    <div class="headerbar q-pa-sm row no-wrap items-center">
                        <QIcon name="mdi-phone" color="positive" size="sm">
                            <QTooltip position="right">Soft phone connected</QTooltip>
                        </QIcon>
                        <span class="q-ml-sm">{this.phoneTitleText}</span>
                        <QSpace />
                        <QBtn flat dense size="sm" icon="mdi-cancel" color="grey" disable>
                            <QTooltip position="right">Toggle Do-Not-Disturb</QTooltip>
                        </QBtn>
                        <QBtn flat dense size="sm" icon="mdi-account-group" color="grey" disable>
                            <QTooltip position="right">Switch to Conference Mode</QTooltip>
                        </QBtn>
                    </div>

                    <div class="inputbar q-ma-sm">
                        <QSelect
                            class="q-mb-sm"
                            outlined
                            dense
                            label="From"
                            options={this.previewFromOptions}
                            optionLabel="text"
                            optionValue="value"
                            emitValue
                            mapOptions
                            modelValue={this.previewFromValue}
                            readonly
                        />
                        <div class="row no-wrap items-center" style={{ gap: '4px' }}>
                            <QSelect
                                class="col"
                                style={{ minWidth: 0 }}
                                outlined
                                dense
                                clearable={false}
                                hideBottomSpace
                                label="Search / Dial"
                                placeholder="Search / Dial"
                                options={[this.previewDialOption]}
                                optionLabel="title"
                                modelValue={this.previewDialOption}
                                emitValue={false}
                                readonly
                            />
                        </div>
                    </div>

                    <div class="dialbar q-ma-sm row no-wrap items-center">
                        <div style={{ flexShrink: 0 }}>
                            <QBtn flat dense size="sm" class="q-ml-sm" icon="mdi-cog" color="primary" round disable>
                                <QTooltip position="right">Phone Settings</QTooltip>
                            </QBtn>
                        </div>
                        <QSpace />
                        <div class="row no-wrap items-center">
                            <QBtn color="primary" unelevated disable>
                                Hold and dial
                            </QBtn>
                            <QBtn color="primary" unelevated icon="mdi-arrow-down-drop-circle" class="text-white" disable>
                                <QMenu anchor="bottom end" self="top end">
                                    <QList dense>
                                        <QItem dense>
                                            <QItemSection>
                                                <QItemLabel>Add call (conference)</QItemLabel>
                                            </QItemSection>
                                        </QItem>
                                        <QItem dense>
                                            <QItemSection>
                                                <QItemLabel>Blind transfer</QItemLabel>
                                            </QItemSection>
                                        </QItem>
                                    </QList>
                                </QMenu>
                            </QBtn>
                        </div>
                    </div>

                    <div class="activecalls q-ma-sm">
                        <div class="generalcallcontrols q-pa-sm" style={generalCallControlsStyle}>
                            <QBtn outline color="primary" icon="mdi-microphone" disable>
                                Mute
                            </QBtn>
                            <QSpace />
                            <QBtn color="negative" unelevated icon="mdi-phone-hangup" disable>
                                End
                            </QBtn>
                        </div>
                        <div class="callcontrolscontainer" style={callControlsContainerStyle}>
                            <div class="callcontrols q-pa-md call_state_normal" style={callRowStyle}>
                                <QBtn color="success" icon="mdi-pause" unelevated noCaps class="text-white" disable>
                                    <QTooltip position="right">Hold</QTooltip>
                                </QBtn>
                                <QIcon name="mdi-volume-high" size="sm" />
                                <ElapsedTime starttime={this.previewCallStart} />
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                                    <span class="from_title" style={{ fontWeight: 'bolder' }}>Jordan Lee</span>
                                    <span class="from_subtitle" style={{ fontSize: 'smaller' }}>(801) 555-0142</span>
                                </div>
                                <QSpace />
                                <QBtn flat dense icon="mdi-dots-vertical" disable>
                                    <QMenu anchor="bottom end" self="top end">
                                        <QList dense>
                                            <QItem dense>
                                                <QItemSection>
                                                    <QItemLabel>Copy Session ID</QItemLabel>
                                                </QItemSection>
                                            </QItem>
                                        </QList>
                                    </QMenu>
                                </QBtn>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
});
